/**
 * What a car has been worth since it was new, and what that makes it.
 *
 * The catalogue carries three things about a car's money and they are not the
 * same kind of thing, which is the whole reason this module exists rather than
 * a component reading `car.price` and drawing it:
 *
 * - `price.launch` — a list price the marque published, in a stated year.
 * - `price.market` — an average of recorded public sales, read in a stated
 *   month.
 * - `price.trend` — the years in between, which nobody published and which
 *   `carValueTrend.js` therefore authors as estimates.
 *
 * `valueHistory` joins the three into one series and LABELS each point with
 * which it is, so the chart can draw a fact and an estimate differently. That
 * labelling is the module's contract: a series that came back unlabelled would
 * be drawn in one weight, and a graph that draws an estimate in the same ink as
 * a document is how a chart lies. It is the same argument `accelerationTrace.js`
 * makes about interpolation, applied to a different kind of gap.
 *
 * `investmentRating` is the second half, and it deliberately does NOT read the
 * estimates. A rating is the one number on this band a reader might repeat to
 * somebody, so it is computed from the two sourced ends and nothing else — the
 * shape of the middle is worth showing and is not worth grading.
 *
 * Pure: numbers and vocabulary values, no React, no DOM, no `t()` — and no
 * `Intl` either, which the rest of the domain does allow itself. Nothing here is
 * formatted. Amounts and rates come back as numbers and `carMoney.js` draws
 * them, because two modules formatting money is two places for a currency to
 * come out differently on two bands of the same card.
 */

/**
 * The compound rate the dollar itself lost over the span this catalogue covers.
 *
 * Every figure in `carPrices.js` is nominal, and a nominal comparison across
 * four decades flatters everything: $85,000 of 1985 money is not $85,000. So the
 * rating is computed in REAL terms, and this is what it is deflated by.
 *
 * One average rate rather than a year-by-year index, and that is a stated
 * approximation rather than an oversight. US consumer prices compounded at
 * roughly this rate from the middle eighties to 2026 — a 1987 dollar buys about
 * a third of what it did — and a forty-row index would be forty more numbers to
 * maintain in service of a distinction none of the four bands below can see: no
 * car in the catalogue sits within a tenth of a percent of a band edge.
 *
 * It is here rather than in content because it is not a fact about a car. It is
 * the yardstick every car is measured against, and a per-car copy of a yardstick
 * is how two cars end up measured differently.
 */
export const ANNUAL_INFLATION = 0.027;

/**
 * How a car has done as an asset, worst to best.
 *
 * A closed set like the schema's vocabularies, and for the same reason — each
 * value reaches a message key (`cars.value.rating.blue-chip`) — but it lives
 * here rather than in `carSchema.js` because it is DERIVED. The schema's
 * vocabularies are what a content pack may author and what `isKnown` refuses
 * outside of; nothing authors a rating, so putting it there would invite a
 * record to state one and a validator to believe it.
 */
export const INVESTMENT_RATINGS = ['soft', 'holding', 'appreciating', 'blue-chip'];

/**
 * The band edges, in REAL compound annual growth over the whole hold.
 *
 * Chosen against what the numbers mean rather than to fill the bands evenly:
 *
 * - **−2% a year** is the point at which a car has visibly failed to keep its
 *   money: over a thirty-year hold it is roughly half of what it cost, in real
 *   terms, and every car under this line in the catalogue is one nobody would
 *   claim was an investment.
 * - **+0.5% a year** is the line between losing to inflation and beating it,
 *   with half a point of slack either side, because a car that landed exactly on
 *   inflation has held its value and saying it appreciated would be overclaiming
 *   on a rounding difference.
 * - **+2% a year** roughly DOUBLES in real terms over a thirty-five year hold.
 *   That is a different kind of object from the rest of the catalogue, and it is
 *   the only band this product is entitled to call blue chip.
 *
 * Note what this rates and what it does not: it is a record of how a car HAS
 * held its money, from list price to today's market, and it is not a forecast.
 * The copy the keys resolve to is written in the past tense for that reason.
 */
const RATING_EDGES = [-0.02, 0.005, 0.02];

/** `2026-08` is a stamp that sorts; the arithmetic needs the year off the front. */
function stampedYear(stamp) {
    const match = /^(\d{4})-\d{2}$/.exec(typeof stamp === 'string' ? stamp : '');

    return match ? Number(match[1]) : null;
}

/**
 * A figure this band can actually DRAW, which is not the same as a number.
 *
 * The currency is checked as hard as the amount, and it has to be: everything
 * downstream formats through `Intl` in currency mode, which answers null for a
 * price with no currency to name. An amount without one therefore renders as a
 * band whose every money label is blank, whose axis reads `Value (undefined)`
 * and whose caption reads, literally, "null put into gold in 1987". Checking
 * only the amount here is what lets that through — a raw `null` printed at a
 * reader, which is the one failure mode this whole band is written against.
 *
 * `validateCar.js` refuses a currency-less price at the build, so it cannot ship
 * — but the catalogue is FETCHED content that changes without a deploy, which is
 * the same argument `valueHistory` makes below for dropping an out-of-span
 * estimate. The band declining to draw is the honest answer; a blank plate is
 * not.
 */
function isMoney(money) {
    return Boolean(money)
        && Number.isFinite(money.amount)
        && money.amount > 0
        && typeof money.currency === 'string'
        && money.currency !== '';
}

/**
 * The two ends, or null if either of them is missing.
 *
 * Both or neither, deliberately. A series with one sourced end is a line running
 * off towards a figure the catalogue has refused to state — which is exactly the
 * Yellowbird, whose twenty-nine cars have no market average to end at — and a
 * chart drawn to a number nobody would print is worse than no chart.
 */
function sourcedEnds(car) {
    const { launch, market } = car?.price || {};
    const marketYear = stampedYear(market?.asOf);

    if (!isMoney(launch) || !isMoney(market) || !Number.isFinite(launch.year) || marketYear === null) {
        return null;
    }

    return { launch, market, launchYear: launch.year, marketYear };
}

/** Is there a value series to draw for this car? */
export function hasValueHistory(car) {
    return sourcedEnds(car) !== null;
}

/**
 * The series, in year order, each point saying whether it is sourced.
 *
 * Estimates that fall outside the sourced span are DROPPED rather than drawn.
 * `validateCar.js` refuses them at the build, so this cannot happen to the
 * shipped catalogue — but the catalogue is fetched content that changes without
 * a deploy, and the failure it would otherwise cause is a point drawn beyond the
 * end of an axis, which reads as a chart bug rather than as bad content.
 *
 * `ceiling` is the extent the chart is drawn to and the baseline is zero, not
 * the cheapest year. Money has a true zero and a value chart floated off one
 * turns a car that lost a fifth of its worth into a cliff.
 */
export function valueHistory(car) {
    const ends = sourcedEnds(car);

    if (!ends) {
        return null;
    }

    const { launch, market, launchYear, marketYear } = ends;
    const estimates = (car.price.trend || [])
        .filter((point) => Number.isFinite(point?.year) && Number.isFinite(point?.amount) && point.amount > 0)
        .filter((point) => point.year > launchYear && point.year < marketYear)
        .map((point) => ({ year: point.year, amount: point.amount, sourced: false }));

    const points = [
        { year: launchYear, amount: launch.amount, sourced: true },
        ...estimates,
        { year: marketYear, amount: market.amount, sourced: true },
    ].sort((one, other) => one.year - other.year);

    return {
        points,
        currency: market.currency,
        from: launchYear,
        to: marketYear,
        ceiling: Math.max(...points.map((point) => point.amount)),
    };
}

/**
 * How the car has held its money, as a band and the figures behind it.
 *
 * Computed from the two SOURCED ends only. The estimated middle is what the
 * chart is for; a rating derived from it would be a grade computed off numbers
 * that carry no source, and it is the one output of this band that reads as a
 * verdict.
 *
 * `real` is the growth after inflation and it is what the band is chosen from.
 * `nominal` and `multiple` come back too because they are what the caption
 * actually says — "3.9× its list price" is the sentence a reader understands,
 * and the real rate is the sentence that makes it honest.
 *
 * A hold of under a year would divide by nothing; every car in the catalogue is
 * decades past that, and the guard is here because `launch.year` is content and
 * content changes without a deploy.
 */
export function investmentRating(car) {
    const ends = sourcedEnds(car);

    if (!ends) {
        return null;
    }

    const { launch, market, launchYear, marketYear } = ends;
    const years = marketYear - launchYear;

    if (years < 1) {
        return null;
    }

    const multiple = market.amount / launch.amount;
    const nominal = multiple ** (1 / years) - 1;
    const real = (1 + nominal) / (1 + ANNUAL_INFLATION) - 1;
    const band = RATING_EDGES.filter((edge) => real >= edge).length;

    return {
        rating: INVESTMENT_RATINGS[band],
        real,
        nominal,
        multiple,
        years,
        fromYear: launchYear,
    };
}

/**
 * Where the money gridlines go on an axis running from zero to `maximum`.
 *
 * A 1–2–5 ladder scaled to the axis rather than `maximum / count`, for the
 * reason `accelerationTrace.axisTicks` gives for its own fixed ladder: a reader
 * uses a gridline to estimate a value off, which they can only do against a
 * number they can hold in their head. It is a separate function from that one
 * because the ladders genuinely differ — a speed axis runs to hundreds and a
 * value axis to millions, so that one's fixed list of steps cannot serve this
 * one and a shared version would have to take the ladder as an argument, which
 * is the whole of what it does.
 *
 * The step is the candidate whose LINE COUNT lands closest to `target`, rather
 * than the first one wide enough. Taking the first wide enough is what leaves
 * the F40 — a 3-million-dollar axis under a 2-million-dollar step — with a
 * single gridline three quarters of the way up, which is a chart a reader cannot
 * read a value off at all. Ties go to the smaller step, because a line too many
 * costs less than a line too few.
 *
 * Returns the ticks WITHOUT the zero, which the baseline already draws.
 */
export function valueTicks(maximum, { target = 3 } = {}) {
    if (!(maximum > 0)) {
        return [];
    }

    const magnitude = 10 ** Math.floor(Math.log10(maximum / target));
    const step = [1, 2, 2.5, 5, 10]
        .map((multiplier) => multiplier * magnitude)
        .reduce((best, candidate) => (
            Math.abs(Math.floor(maximum / candidate) - target) < Math.abs(Math.floor(maximum / best) - target)
                ? candidate
                : best
        ));

    const ticks = [];

    for (let value = step; value <= maximum; value += step) {
        ticks.push(value);
    }

    return ticks;
}

/**
 * Where the year gridlines go: the decade boundaries inside the span.
 *
 * Decades rather than an even division of the span, because this axis is made of
 * years and a year is a NAME. "1990, 2000, 2010" is a scale a reader already
 * has; "1987, 1997, 2007" is arithmetic they have to do to place anything on it.
 *
 * Strictly inside, so neither end is labelled twice — the corner of the plot is
 * the launch year and the far edge is the month the market was read, and both
 * are said in words in the caption underneath.
 */
export function decadeTicks(from, to) {
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
        return [];
    }

    const ticks = [];

    for (let year = Math.ceil((from + 1) / 10) * 10; year < to; year += 10) {
        ticks.push(year);
    }

    return ticks;
}
